import { memo, useEffect } from "react";
import { Box, Button, CircularProgress, Typography, Paper, Stack, Chip } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { FETCH_SERVICE_JOB } from "@services/graphql/queries/fetchServiceJob";
import { FETCH_SERVICE_JOBS } from "@services/graphql/queries/fetchServiceJobs";
import { ServiceCreditType, ServiceJobStatus } from "@/app/graphql/codegen";
import AIServicesNavbar from "../components/AIServicesNavbar";
import { RouteTo } from "@routing/RouteMap";
import { Grid2 } from "@mui/material";
import { useDispatch } from "react-redux";
import { handleErrorWithNotification } from "@/utils/errorHandling";

const POLL_INTERVAL_MS = 5000;

const VsmeJobStatusPage = memo(() => {
    const dispatch = useDispatch();
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const { data: jobData, loading: jobLoading, error: jobError, refetch: refetchJob } = useQuery(FETCH_SERVICE_JOB, {
        variables: { id },
        skip: !id || id === "current",
        pollInterval: id && id !== "current" ? POLL_INTERVAL_MS : 0,
        errorPolicy: "all",
    });

    const { data: listData, loading: listLoading, error: listError, refetch: refetchList } = useQuery(FETCH_SERVICE_JOBS, {
        variables: { type: ServiceCreditType.VsmeReport, status: ServiceJobStatus.Pending },
        skip: id !== "current",
        pollInterval: id === "current" ? POLL_INTERVAL_MS : 0,
        errorPolicy: "all",
    });

    const job = id === "current" ? listData?.serviceJobs?.[0] : jobData?.serviceJob;
    const loading = id === "current" ? listLoading : jobLoading;

    useEffect(() => {
        if (jobError) {
            handleErrorWithNotification(jobError as Error, dispatch, "Fehler", "Der Report-Job konnte nicht geladen werden");
        }
    }, [jobError, dispatch]);

    useEffect(() => {
        if (listError) {
            handleErrorWithNotification(listError as Error, dispatch, "Fehler", "Die aktuellen Report-Jobs konnten nicht geladen werden");
        }
    }, [listError, dispatch]);

    if (job?.status === "COMPLETED") {
        navigate(`${RouteTo.AI_SERVICES}/vsme/result/${job.id}`);
    }

    return (
        <Box sx={{ p: 3 }}>
            <Grid2 container spacing={3}>
                <Grid2 size={{ xs: 12, md: 3 }}>
                    <AIServicesNavbar />
                </Grid2>

                <Grid2 size={{ xs: 12, md: 9 }}>
                    <Paper elevation={3} sx={{ p: { xs: 3, md: 4 } }}>
                        <Stack spacing={3} alignItems="center">
                            <Typography variant="h4" textAlign="center">
                                Report wird generiert …
                            </Typography>

                            {loading || !job ? (
                                <CircularProgress size={64} />
                            ) : (
                                <>
                                    <Chip
                                        label={job.status}
                                        color={job.status === "IN_PROGRESS" ? "info" : job.status === "PENDING" ? "warning" : "success"}
                                        variant="outlined"
                                        sx={{ fontSize: "1rem", px: 2, py: 0.5 }}
                                    />

                                    <Typography variant="body2" textAlign="center">
                                        Eingereicht am: {new Date(job.submittedAt).toLocaleString()}
                                    </Typography>
                                    {job.estimatedCompletion && (
                                        <Typography variant="body2" textAlign="center">
                                            Voraussichtliches Ende: {new Date(job.estimatedCompletion).toLocaleString()}
                                        </Typography>
                                    )}

                                    <Typography variant="body1" textAlign="center" sx={{ maxWidth: 420 }}>
                                    Du kannst diese Seite verlassen, wir melden uns bei Dir, wenn die Analyse fertig ist
                                    </Typography>

                                    <Stack direction="row" spacing={2}>
                                        <Button variant="outlined" onClick={() => (id === "current" ? refetchList() : refetchJob())}>
                                            Jetzt aktualisieren
                                        </Button>
                                    </Stack>
                                </>
                            )}
                        </Stack>
                    </Paper>
                </Grid2>
            </Grid2>
        </Box>
    );
});

export default VsmeJobStatusPage; 